import { createRoot } from 'react-dom/client';
import { BrandVariants, createDarkTheme, createLightTheme, FluentProvider, Spinner, Theme } from '@fluentui/react-components';
import { BrowserRouter, Route, Routes } from 'react-router';
import OuterTemplate from './OuterTemplate';
import Error404 from './Error404';
import NavContextProvider from './contexts/NavContextProvider';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import BuildsPage from './pages/BuildsPage';
import FlightOpsPage from './pages/FlightOpsPage';
import ExperimentsPage from './pages/ExperimentsPage';
import TelemetryPage from './pages/TelemetryPage';
import SettingsPage from './pages/SettingsPage';
import FlightOpsBranchPage from './pages/FlightOpsBranchPage';
import FlightOpsRingPage from './pages/FlightOpsRingPage';
import FlightOpsBuildPage from './pages/FlightOpsBuildPage';
import HelpPage from './pages/HelpPage';

const container = document.getElementById( "app" )!;
const root = createRoot( container );

const mcTheme: BrandVariants = { 
  10: "#040206",
  20: "#1A1425",
  30: "#272043",
  40: "#302B5C",
  50: "#363677",
  60: "#394393",
  70: "#3950B0",
  80: "#325FCD",
  90: "#206EEA",
  100: "#147EFF",
  110: "#438DFF",
  120: "#609DFF",
  130: "#79ACFF",
  140: "#90BCFF",
  150: "#A8CBFF",
  160: "#BFDAFF"
};

const lightTheme: Theme = {
  ...createLightTheme( mcTheme ), 
};

const darkTheme: Theme = {
  ...createDarkTheme( mcTheme ), 
};

darkTheme.colorBrandForeground1 = mcTheme[ 110 ];
darkTheme.colorBrandForeground2 = mcTheme[ 120 ];

let theme = lightTheme;
const forceTheme = localStorage.getItem( "forceTheme" );
if( forceTheme == "dark" ) theme = darkTheme;
else if( forceTheme != "light" && window.matchMedia( "(prefers-color-scheme: dark)" ).matches ) theme = darkTheme;

theme.fontFamilyBase = "Manrope, sans-serif";
document.body.style.backgroundColor = theme.colorNeutralBackground1;

const authToken = localStorage.getItem( "authToken" );
if( !authToken ) {
  const searchParams = new URLSearchParams( window.location.search );

  if( searchParams.get( "authToken" ) ) {
    localStorage.setItem( "authToken", searchParams.get( "authToken" )! );
    window.location.href = window.location.pathname; // Reload without query params
  } else {
    window.location.href = "https://local.identity.violetech.co.uk/auth/login?returnTo=" + encodeURIComponent( window.location.href );
  }

  throw ""; // Stop execution
}

export const updateDocumentTitle = (
  pageTitle: string
) => {
  document.title = pageTitle + " - Mission Control";
};

root.render(
  <FluentProvider theme={theme}>
    <div>
      <Spinner size="large" label={"Please wait..."} />
    </div>
  </FluentProvider>
);

//
// Validate auth token before loading content
//
fetch(
  "https://api.violetech.co.uk/v1/session",
  {
    method: "GET",
    headers: {
      "Authorization": "Bearer " + localStorage.getItem( "authToken" )!,
      "Content-Type": "application/json"
    }
  }
).then(
  async ( d ) => {
    const r = await d.json();
    if( d.status != 200 ) {
      localStorage.clear();
      window.location.href = "https://local.identity.violetech.co.uk/auth/login?returnTo=" + encodeURIComponent( window.location.href );
      return;
    }

    // @ts-ignore
    globalThis[ "session" ] = r.data;
    
    root.render(
      <NavContextProvider>
        <FluentProvider theme={theme}>
          <BrowserRouter>
            <Routes>
              <Route element={<OuterTemplate />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/builds" element={<BuildsPage />} />
                <Route path="/flightops" element={<FlightOpsPage />} />
                <Route path="/flightops/branches/:branchName" element={<FlightOpsBranchPage />} />
                <Route path="/flightops/rings/:ringName" element={<FlightOpsRingPage />} />
                <Route path="/flightops/builds/:buildId" element={<FlightOpsBuildPage />} />
                <Route path="/experiments" element={<ExperimentsPage />} />
                <Route path="/telemetry" element={<TelemetryPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="*" element={<Error404 />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </FluentProvider>
      </NavContextProvider>
    );
  }
);